import reactUpdate from 'immutability-helper';
import _ from 'underscore';

import FolderDefaults, {FolderDefaultsKey} from 'FolderDefaults';
import {BenchlingAction} from 'redux-stores/Action';
import * as WorkflowUtil from 'workflows/util/WorkflowUtil';
import {WorkflowTemplate, WorkflowTemplateVersion} from 'workflows/WorkflowConstants';

import {CreateWorkflowState, WorkflowTemplateSelectionTableRow} from './WorkflowTemplateSelectionConstants';
import {WorkflowTemplateSelectionActionType} from './WorkflowTemplateSelectionReduxActions';

export interface WorkflowTemplateSelectionState {
  selectedWorkflowTemplateId: string | null;
  selectedWorkflowTemplateVersionIdByTemplateId: {[workflowTemplateId: string]: string};
  rootFolderId: string | null;
}

function workflowTemplateSelectionInitalState(): WorkflowTemplateSelectionState {
  return {
    selectedWorkflowTemplateId: null,
    selectedWorkflowTemplateVersionIdByTemplateId: {},
    rootFolderId: FolderDefaults.get(FolderDefaultsKey.WORKFLOWS)!,
  };
}

/**
 * A short-lived object that has easy access to all data dependencies and exposes some pure
 * functions to compute information about the UI.
 *
 * In a certain sense, this is structurally similar to a React component. The workflow template and
 * workflow template version fields are the equivalent of the props (the externally-owned data
 * dependencies), and the state field is equivalent to the state: the UI state managed by this file.
 * Rather than rendering to HTML, getUiState returns a JS object with all of the presentation
 * details figured out so that the React render step can be as simple as possible.
 */
class WorkflowTemplateSelectionContext {
  workflowTemplates: Array<WorkflowTemplate> | null;
  workflowTemplateVersionsById: {[id: string]: WorkflowTemplateVersion};
  state: WorkflowTemplateSelectionState;

  constructor(
    workflowTemplates: Array<WorkflowTemplate> | null,
    workflowTemplateVersionsById: {[id: string]: WorkflowTemplateVersion},
    state: WorkflowTemplateSelectionState
  ) {
    // Models provided by the store.
    this.workflowTemplates = workflowTemplates;
    this.workflowTemplateVersionsById = workflowTemplateVersionsById;
    this.state = state;
  }

  getUiState(): CreateWorkflowState {
    const selectedWorkflowTemplateVersionId = this._getSelectedWorkflowTemplateVersionId();
    return {
      isLoading: this.workflowTemplates == null,
      tableRows: this._generateTableRowData(),
      selectedWorkflowTemplateVersionId,
      rootFolderId: this.state.rootFolderId,
    };
  }

  /**
   * Get the resolved selections for workflow template versions, accounting for defaults.
   */
  _getWorkflowTemplateVersionSelections(): {[workflowTemplateId: string]: string} {
    if (!this.workflowTemplates) {
      return {};
    }
    return _.object(
      this.workflowTemplates.map((workflowTemplate) => {
        const explicitSelection =
          this.state.selectedWorkflowTemplateVersionIdByTemplateId[workflowTemplate.id];
        if (explicitSelection) {
          return [workflowTemplate.id, explicitSelection];
        }
        // Default to the most recent published version.
        const workflowTemplateVersion =
          WorkflowUtil.getLatestPublishedWorkflowTemplateVersion(workflowTemplate);
        return [workflowTemplate.id, workflowTemplateVersion ? workflowTemplateVersion.id : null];
      })
    );
  }

  _getSelectedWorkflowTemplateVersionId(): string | null {
    const {selectedWorkflowTemplateId} = this.state;
    if (selectedWorkflowTemplateId == null) {
      return null;
    }
    return this._getWorkflowTemplateVersionSelections()[selectedWorkflowTemplateId];
  }

  /**
   * Returns an array of objects satisfying WORKFLOW_TEMPLATE_SELECTION_TABLE_ROW_SHAPE.
   */
  _generateTableRowData(): Array<WorkflowTemplateSelectionTableRow> | null {
    if (this.workflowTemplates == null) {
      return null;
    }
    const selectedTemplateVersions = this._getWorkflowTemplateVersionSelections();
    return _.sortBy(this.workflowTemplates, 'name')
      .map(({id: workflowTemplateId, name, workflowTemplateVersions: workflowTemplateVersionSummaries}) => {
        const selectedVersionId = selectedTemplateVersions[workflowTemplateId];
        const validTemplateVersions = workflowTemplateVersionSummaries.filter(
          // eslint-disable-next-line benchlint/no-eqeqeq-null
          (templateVersion) => templateVersion.version !== null
        );
        return {
          isSelected: workflowTemplateId === this.state.selectedWorkflowTemplateId,
          workflowTemplateId,
          workflowTemplateName: name,
          selectedVersionId,
          templateVersions: validTemplateVersions,
        };
      })
      .filter((row) => row.templateVersions.length > 0);
  }
}

export function getUiState(
  workflowTemplates: Array<WorkflowTemplate> | null,
  workflowTemplateVersionsById: {[id: string]: WorkflowTemplateVersion},
  state: WorkflowTemplateSelectionState
): CreateWorkflowState {
  return new WorkflowTemplateSelectionContext(
    workflowTemplates,
    workflowTemplateVersionsById,
    state
  ).getUiState();
}

/**
 * Given the old (immutable) state and an action, return the new state.
 */
export default function reduceWorkflowTemplateSelection(
  state: WorkflowTemplateSelectionState = workflowTemplateSelectionInitalState(),
  action: BenchlingAction
): WorkflowTemplateSelectionState {
  switch (action.type) {
    case WorkflowTemplateSelectionActionType.RESET_STATE: {
      return workflowTemplateSelectionInitalState();
    }

    case WorkflowTemplateSelectionActionType.SELECT_WORKFLOW_TEMPLATE: {
      const {workflowTemplateId} = action;
      if (state.selectedWorkflowTemplateId === workflowTemplateId) {
        return state;
      } else {
        return reactUpdate(state, {
          selectedWorkflowTemplateId: {
            $set: workflowTemplateId,
          },
        });
      }
    }

    case WorkflowTemplateSelectionActionType.UPDATE_ROOT_FOLDER: {
      const {rootFolderId} = action;
      return reactUpdate(state, {$merge: {rootFolderId}});
    }

    case WorkflowTemplateSelectionActionType.SELECT_WORKFLOW_TEMPLATE_VERSION: {
      const {workflowTemplateId, workflowTemplateVersionId} = action;
      if (state.selectedWorkflowTemplateId !== workflowTemplateId) {
        state = reactUpdate(state, {
          selectedWorkflowTemplateId: {
            $set: workflowTemplateId,
          },
        });
      }
      return reactUpdate(state, {
        selectedWorkflowTemplateVersionIdByTemplateId: {
          [workflowTemplateId]: {
            $set: workflowTemplateVersionId,
          },
        },
      });
    }

    default:
      return state;
  }
}
